import { notifyError } from "@/lib/notify-error";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { ArrowLeftRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { JobListPage, jobListDateColumn, type JobListColumn } from "@/components/layout";
import { ComparisonStatusBadge } from "@/components/comparison-status-badge";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { RowAction, RowActionsMenu } from "@/components/row-actions-menu";
import { Badge } from "@/components/ui/badge";
import { InlineEditNameCell } from "@/components/inline-edit-name-cell";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useProject } from "@/components/projects";
import { RequiresCapability } from "@/lib/capabilities";

type ComparisonKind = "schedule" | "plan_set";

const COMPARISON_KIND_LABELS: Record<ComparisonKind, string> = {
  schedule: "Schedule",
  plan_set: "Plan Set",
};

const NEW_COMPARISON_LABEL = "New RevPilot Comparison";

export const Route = createFileRoute("/comparisons/")({
  component: ComparisonsPage,
});

export function ComparisonsPage() {
  const { projectId, isLoading: isProjectLoading } = useProject();
  const { data: comparisons } = useQuery({
    ...convexQuery(api.scheduleComparisons.listWithStats, { projectId: projectId! }),
    enabled: !!projectId,
  });
  const navigate = useAppNavigate();
  const removeComparison = useMutation(api.scheduleComparisons.remove);
  const updateName = useMutation(api.scheduleComparisons.updateName);
  const [deletingComparisonIds, setDeletingComparisonIds] = useState<Set<string>>(
    () => new Set()
  );

  const handleNewRevPilot = () => {
    navigate({ to: "/revPilot/new", title: NEW_COMPARISON_LABEL });
  };

  const handleDeleteComparison = async (
    comparisonId: Id<"revPilotScheduleComparisons">
  ) => {
    setDeletingComparisonIds((current) => {
      const next = new Set(current);
      next.add(String(comparisonId));
      return next;
    });

    try {
      await removeComparison({ comparisonId });
      toast.success("Comparison deleted");
    } catch (error) {
      notifyError(error, "Failed to delete comparison");
      console.error(error);
    } finally {
      setDeletingComparisonIds((current) => {
        const next = new Set(current);
        next.delete(String(comparisonId));
        return next;
      });
    }
  };

  const handleUpdateName = async (
    comparisonId: Id<"revPilotScheduleComparisons">,
    name: string
  ) => {
    try {
      await updateName({ comparisonId, name });
      toast.success("Name updated");
    } catch (error) {
      notifyError(error, "Failed to update name");
      console.error(error);
      throw error;
    }
  };

  // Built per render: `@/components/layout` and this route import each other through the
  // workspace route manifest, so module-level calls into the layout barrel can run too early.
  const columns: JobListColumn<ComparisonListItem>[] = [
    {
      id: "kind",
      header: "Kind",
      cell: (comparison) => (
        <Badge variant="outline" size="sm">
          {COMPARISON_KIND_LABELS[comparison.comparisonKind] ?? "Schedule"}
        </Badge>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (comparison) => <ComparisonStatusBadge status={comparison.status} />,
    },
    {
      id: "changed-rows",
      header: "Changed Rows",
      variant: "number",
      cell: (comparison) => comparison.stats?.changedRows ?? 0,
    },
    jobListDateColumn("Created", (comparison) => comparison.createdAt),
  ];

  return (
    <JobListPage
      title="RevPilot"
      newAction={{ label: NEW_COMPARISON_LABEL, onClick: handleNewRevPilot }}
      emptyState={{
        icon: ArrowLeftRight,
        title: "No RevPilot comparisons yet",
        description:
          "Compare two revisions of a schedule or plan set from your project documents to see what changed.",
      }}
      isLoading={isProjectLoading || comparisons === undefined}
      rows={(comparisons ?? []) as ComparisonListItem[]}
      getRowKey={(comparison) => comparison._id}
      getRowName={(comparison) => comparison.name}
      getRowLink={(comparison) => ({
        to: "/revPilot/$comparisonId",
        params: { comparisonId: comparison._id },
        title: comparison.name,
      })}
      renderName={(comparison) => (
        <InlineEditNameCell
          value={comparison.name}
          onSave={(name) => handleUpdateName(comparison._id, name)}
        />
      )}
      columns={columns}
      renderActions={(comparison) => (
        <RequiresCapability mode="hide">
          <ComparisonActions
            comparison={comparison}
            isDeleting={deletingComparisonIds.has(String(comparison._id))}
            onDelete={handleDeleteComparison}
          />
        </RequiresCapability>
      )}
    />
  );
}

interface ComparisonListItem {
  _id: Id<"revPilotScheduleComparisons">;
  name: string;
  comparisonKind: ComparisonKind;
  status: string;
  stats?: { changedRows?: number } | null;
  createdAt: number;
}


interface ComparisonActionsProps {
  comparison: ComparisonListItem;
  isDeleting: boolean;
  onDelete: (comparisonId: Id<"revPilotScheduleComparisons">) => Promise<void>;
}

function ComparisonActions({ comparison, isDeleting, onDelete }: ComparisonActionsProps) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  return (
    <>
      <RowActionsMenu label={`Actions for ${comparison.name}`} disabled={isDeleting}>
        <RowAction icon={Trash2} destructive onSelect={() => setConfirmDeleteOpen(true)}>
          Delete
        </RowAction>
      </RowActionsMenu>
      <ConfirmationDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete Comparison"
        description={`Are you sure you want to delete "${comparison.name}"? This action cannot be undone.`}
        onConfirm={() => onDelete(comparison._id)}
        disabled={isDeleting}
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </>
  );
}
